const ADDED_STATE_MS = 2000;
const INERT_MS = 1000;
// Length of the label/check-icon fade. Must match --sample-added-duration in
// sample-toast.css, which is also what the button's background, border and text
// color transition on — the point of the shared clock is that the swapped-in
// content and the green arrive together. Only used to drop the class once the
// animation has finished.
const CONTENT_TRANSITION_MS = 250;

// Mirrors the 'check' case in theme/snippets/icon.liquid — a bare stroked tick,
// not the filled-circle 'success' icon, which is the wrong shape once the button
// itself is already green. Duplicated here (rather than rendered by Liquid) since
// the Added state is JS-driven and transient, not part of the server-rendered markup.
const CHECK_ICON_MARKUP =
    '<svg role="presentation" focusable="false" width="14" height="14" viewBox="0 0 14 14" ' +
    'class="icon icon-check sample-add__check-icon" aria-hidden="true">' +
    '<path d="M2.5 7.3L5.62937 10.4L11.5 4" stroke="currentColor" stroke-width="1.6" ' +
    'stroke-linecap="round" stroke-linejoin="round" fill="none"></path></svg>';

// <sample-add> wraps a sample's product-form + submit button (product-card.liquid,
// on every page except a PDP — see docs/adr/adr-20260729-01-product-card-sample-adds-skip-cart-drawer.md).
// Where it renders, it renders unconditionally — see the `sample-add { display:
// block; }` rule in theme.css.
//
// On its form's `variant:add` (which bubbles, so this ancestor listener runs before
// CartDrawer's `document` listener — see
// docs/adr/adr-20260729-01-product-card-sample-adds-skip-cart-drawer.md) it sets
// `detail.blockCartDrawerOpening`, drives a transient green Added state on its
// button, and re-dispatches `sample:added` on `document` so `<sample-toast>` — which
// knows nothing about forms or buttons — can react.
class SampleAdd extends HTMLElement {
    connectedCallback() {
        this._onVariantAdd = this._onVariantAdd.bind(this);
        this._onCartError = this._onCartError.bind(this);
        this._onClick = this._onClick.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this.addEventListener('variant:add', this._onVariantAdd);
        this.addEventListener('cart:error', this._onCartError);
        this.addEventListener('click', this._onClick, true);
        this.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
        clearTimeout(this._inertTimer);
        clearTimeout(this._revertTimer);
        clearTimeout(this._contentTimer);
        if (this._busyObserver) this._busyObserver.disconnect();
        this.removeEventListener('variant:add', this._onVariantAdd);
        this.removeEventListener('cart:error', this._onCartError);
        this.removeEventListener('click', this._onClick, true);
        this.removeEventListener('submit', this._onSubmit);
    }

    get _button() {
        return this.querySelector('[type="submit"]');
    }

    get _label() {
        return this.querySelector('[data-sample-add-label]');
    }

    // theme.js's ProductForm removes the button's `aria-busy` as soon as
    // /cart/add.js resolves, but it then fetches /cart.js before dispatching
    // `variant:add`. CustomButton reacts to that removal immediately, so across
    // that gap the button plays its full content-return animation and shows the
    // *stale* "Add Sample" label again — a beat before the Added state replaces it.
    // Two text animations back to back, which is the stutter UAT saw.
    //
    // `--pending` freezes the button's busy visuals across that gap: the spinner
    // stays up and the old label never comes back, so the result arrives as one
    // motion instead of two.
    //
    // It is applied when the attribute is removed, not on submit, so the theme's
    // *entrance* animation (label out, spinner in) still plays normally — masking
    // from submit collapsed that into a hard snap. A MutationObserver callback is a
    // microtask, so it lands before the browser paints the un-busied frame and the
    // stale label is never visible. Deliberately not re-asserting `aria-busy`
    // instead: that restarts CustomButton's timeline and makes the dots slide.
    //
    // Both terminal paths clear it — `variant:add` on success, `cart:error` on
    // failure. A request that resolves as neither (a rejected fetch) already leaves
    // the theme's own spinner up forever, so this adds no new stuck state.
    _onSubmit() {
        const button = this._button;
        if (!button) return;

        this._awaitingResult = true;
        if (!this._busyObserver) {
            this._busyObserver = new MutationObserver(() => {
                if (this._awaitingResult && button.getAttribute('aria-busy') !== 'true') {
                    this.classList.add('sample-add--pending');
                    this._detachFromButtonTimeline(button);
                }
            });
        }
        this._busyObserver.observe(button, { attributeFilter: ['aria-busy'] });
    }

    // Take the spinner and the content wrapper away from CustomButton's timeline for
    // the rest of this add, so the rules in sample-toast.css own them outright.
    //
    // Two reasons a plain opacity mask was not enough. The un-busy timeline animates
    // `transform` as well as opacity, so the slide kept running underneath it and the
    // dots rode up 10px while pinned visible — measured on local dev as translateY(0)
    // → translateY(-10px) across the hold — then cut out from the raised position.
    // And an animation still filling a property suppresses any transition on it, so
    // the spinner could not fade out even with both ends declared `!important`.
    //
    // Cancelling alone is a trap: Motion One commits each finished segment's values
    // as *inline styles*, so killing the exit timeline just exposes what the entrance
    // timeline already wrote (loader `opacity: 1`, wrapper `opacity: 0`) and the
    // button ends up parked on a full-opacity spinner with an invisible label. The
    // inline values have to be cleared too. Base CSS underneath is already the
    // correct resting state — `.button__loader` is `opacity: 0` in theme.css and the
    // wrapper is unstyled — so both land right once this add is over.
    //
    // The dots' pulse lives on the loader's *children* and getAnimations() is not a
    // subtree query, so the spinner keeps spinning while it is held.
    _detachFromButtonTimeline(button) {
        const targets = [button.querySelector(':scope > div'), button.querySelector('.button__loader')];

        for (const element of targets) {
            if (!element) continue;
            // Guarded because jsdom implements no Web Animations API.
            if (typeof element.getAnimations === 'function') {
                element.getAnimations().forEach((animation) => animation.cancel());
            }
            element.style.removeProperty('transform');
            element.style.removeProperty('opacity');
        }
    }

    _releasePending() {
        this._awaitingResult = false;
        if (this._busyObserver) this._busyObserver.disconnect();
        this.classList.remove('sample-add--pending');
    }

    _onVariantAdd(event) {
        event.detail.blockCartDrawerOpening = true;
        this._releasePending();
        this._showAdded();
        document.dispatchEvent(new CustomEvent('sample:added', { bubbles: true }));
    }

    _onCartError(event) {
        this._releasePending();
        const error = event.detail && event.detail.error;
        document.dispatchEvent(new CustomEvent('sample:add-failed', { bubbles: true, detail: { error } }));
    }

    // `aria-disabled` is advisory only — it does not block activation on its own,
    // so the click guard below must also preventDefault. Using `pointer-events: none`
    // instead would be invisible to screen readers and would not stop keyboard Enter.
    _onClick(event) {
        const button = this._button;
        if (button && button.getAttribute('aria-disabled') === 'true') {
            event.preventDefault();
        }
    }

    _showAdded() {
        const button = this._button;
        const label = this._label;

        clearTimeout(this._inertTimer);
        clearTimeout(this._revertTimer);

        this.classList.add('sample-add--added');
        if (button) {
            button.setAttribute('aria-disabled', 'true');
            if (!button.querySelector('.sample-add__check-icon')) {
                button.insertAdjacentHTML('afterbegin', CHECK_ICON_MARKUP);
            }
        }
        if (label) {
            if (this._originalLabel === undefined) {
                this._originalLabel = label.textContent;
            }
            const strings = window.themeVariables && window.themeVariables.strings;
            label.textContent = (strings && strings.sampleAdded) || this._originalLabel;
        }
        this._playContentTransition();

        this._inertTimer = setTimeout(() => {
            if (button) button.removeAttribute('aria-disabled');
        }, INERT_MS);

        this._revertTimer = setTimeout(() => {
            this.classList.remove('sample-add--added');
            const icon = button && button.querySelector('.sample-add__check-icon');
            if (icon) icon.remove();
            if (label && this._originalLabel !== undefined) {
                label.textContent = this._originalLabel;
            }
            this._playContentTransition();
        }, ADDED_STATE_MS);
    }

    // Fades the label (and the check icon, when entering) in over the same
    // duration and easing the button's colors transition on, so a step change no
    // transition can smooth — replacing the text, inserting the icon — lands with
    // the color instead of a beat ahead of it. Runs in both directions: entering
    // the Added state and reverting out of it.
    //
    // Toggling the class off/on in one tick is a no-op without a forced reflow, so
    // a second add during the green window would otherwise not replay the fade.
    // Same pattern as _playEntrance() in sample-toast.js.
    _playContentTransition() {
        clearTimeout(this._contentTimer);
        this.classList.remove('sample-add--content-in');
        void this.offsetWidth;
        this.classList.add('sample-add--content-in');

        this._contentTimer = setTimeout(() => {
            this.classList.remove('sample-add--content-in');
        }, CONTENT_TRANSITION_MS);
    }
}

if (!window.customElements.get('sample-add')) {
    window.customElements.define('sample-add', SampleAdd);
}
